import { useRef } from "react";
import { createPortal } from "react-dom";
import { X } from "lucide-react";
import { useOverlay } from "@/lib/overlay";

// Rendered at body level (portal): fixed full screen whatever the scroll position; unmounted when closed.
// side: right panel 420 px on desktop, bottom sheet 86 % on mobile (quests and Jo), prototype header with the Jo avatar.
export default function Modal({ open, onClose, title, children, testId = "modal", grid = false, side = false }) {
  const card = useRef(null);
  useOverlay(open, onClose, card);
  if (!open) return null;
  const wrap = side ? "fixed inset-0 z-50 flex items-end justify-end" : "fixed inset-0 z-50 flex items-end justify-center sm:items-center sm:p-6";
  const box = side
    ? "slide-in relative flex h-[86dvh] w-full flex-col overflow-hidden rounded-t-[20px] border-t border-line bg-surface pb-[env(safe-area-inset-bottom)] shadow-modal outline-none md:h-full md:w-[420px] md:rounded-none md:border-l md:border-t-0"
    : `fade-up relative max-h-[92dvh] w-full overflow-y-auto rounded-t-[14px] border border-line bg-surface p-6 pb-[calc(1.5rem+env(safe-area-inset-bottom))] shadow-modal outline-none sm:max-w-[440px] sm:rounded-card sm:pb-6 ${grid ? "bg-[color:var(--dot-bg)]" : ""}`;
  const close = (
    <button type="button" onClick={onClose} aria-label="Fermer" data-testid={`${testId}-close`}
      className={side ? "ml-auto grid h-9 w-9 shrink-0 place-items-center rounded-full border border-line bg-surface text-ink hover:bg-surface-2" : "-mr-2 -mt-1 grid h-9 w-9 shrink-0 place-items-center rounded-full text-muted hover:bg-surface-2 hover:text-ink"}>
      <X className="h-[17px] w-[17px]" strokeWidth={2} />
    </button>
  );
  return createPortal(
    <div className={wrap} data-testid={testId} data-overlay={side ? "panel" : "modal"}>
      <button
        type="button"
        tabIndex={-1}
        aria-label="Fermer"
        className="overlay-fade absolute inset-0 bg-black/70"
        onClick={onClose}
        data-testid={`${testId}-backdrop`}
      />
      <div
        ref={card}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        tabIndex={-1}
        className={box}
      >
        {grid && <div className="dot-layer" aria-hidden="true" />}
        {side ? (
          <>
            <div className="flex items-center gap-2.5 border-b border-line px-[18px] py-4">
              <span className="grid h-8 w-8 shrink-0 place-items-center rounded-full bg-ink text-[14px] font-semibold text-bg" aria-hidden="true">J</span>
              <h2 className="text-[15px] font-semibold text-ink">{title}</h2>
              {close}
            </div>
            <div className="relative flex min-h-0 flex-1 flex-col overflow-y-auto p-[18px]">{children}</div>
          </>
        ) : (
          <>
            <div className="relative mb-5 flex items-start justify-between gap-4">
              <h2 className="text-lg font-semibold tracking-[-0.01em] text-ink">{title}</h2>
              {close}
            </div>
            <div className="relative">{children}</div>
          </>
        )}
      </div>
    </div>,
    document.body,
  );
}
